import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NavigationTabs } from '../../../components/NavigationTabs/NavigationTabs';
import { fetchProductById } from '../../../api/productApi';
import { ProductDetail } from '../../../types/product';
import { LoadingIndicator } from '../../../components/LoadingIndicator/LoadingIndicator';
import styles from './MyPurchase.module.scss';

export const MyPurchase: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [product, setProduct] = useState<ProductDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadProduct = async () => {
            if (!id) return;

            try {
                const productData = await fetchProductById(id);
                if (!productData.purchased) {
                    throw new Error('Курс не приобретен');
                }
                setProduct(productData);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Не удалось загрузить курс');
            } finally {
                setLoading(false);
            }
        };

        loadProduct();
    }, [id]);

    const handleVideoSelect = (index: number) => {
        setCurrentVideoIndex(index);
    };

    const handleTabChange = (tab: 'products' | 'favorites' | 'account') => {
        if (tab === 'products') navigate('/');
        else if (tab === 'favorites') navigate('/favorites');
        else if (tab === 'account') navigate('/account');
    };

    const handleBackClick = () => {
        navigate('/my-purchases');
    };

    if (loading) {
        return <LoadingIndicator message="Загрузка курса..." />;
    }

    if (error || !product) {
        return (
            <div className={styles.notFound}>
                <h2>{error || 'Курс не найден'}</h2>
                <button onClick={() => navigate('/my-purchases')}>
                    Вернуться к покупкам
                </button>
            </div>
        );
    }

    const currentVideo = product.videos[currentVideoIndex];

    return (
        <div className={styles.container}>


            <div className={styles.playerContainer}>
                <button
                    className={styles.backButton}
                    onClick={handleBackClick}
                    style={{width: '88px', height: '33px'}}
                >
                    ← Назад
                </button>


                <div className={styles.videoWrapper} style={{paddingTop: '56.25%'}}>
                    <iframe
                        src={currentVideo.url}
                        allow="autoplay; fullscreen; picture-in-picture; encrypted-media; gyroscope; accelerometer; clipboard-write; screen-wake-lock;"
                        frameBorder="0"
                        allowFullScreen
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%'
                        }}
                    ></iframe>
                </div>

                <div className={styles.purchaseInfo}>
                    <div className={styles.purchaseBadge}>
                        <div className={styles.checkCircle}>
                            <span className={styles.checkIcon}>✓</span>
                        </div>
                        <span className={styles.purchaseText}>Приобретено</span>
                    </div>
                    <h1 className={styles.videoTitle}>{currentVideo.title}</h1>
                    <p className={styles.videoDuration}>{formatDuration(currentVideo.duration)}</p>
                </div>
            </div>

            <div className={styles.videosList}>
                {product.videos.map((video, index) => (
                    <div
                        key={video.id}
                        className={`${styles.videoItem} ${index === currentVideoIndex ? styles.active : ''}`}
                        onClick={() => handleVideoSelect(index)}
                    >
                        <div className={styles.videoInfo}>
                            <h3 className={styles.itemTitle}>{video.title}</h3>
                            <p className={styles.itemDuration}>{formatDuration(video.duration)}</p>
                        </div>
                        <div className={styles.videoThumbnail}>
                            <img
                                src={video.thumbnail}
                                alt={video.title}
                                className={styles.thumbnailImage}
                            />
                        </div>
                    </div>
                ))}
            </div>

            <div className={styles.descriptionContainer}>
                <h2 className={styles.descriptionTitle}>Описание курса</h2>
                <p className={styles.descriptionText}>{product.description}</p>
            </div>

            <div className={styles.navigation}>
                <NavigationTabs
                    activeTab="products"
                    onTabChange={handleTabChange}
                />
            </div>
        </div>
    );
};

const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};